<script setup>
import { ref, watch, nextTick } from 'vue';
import { usePaymentsStore } from '@/stores/payments';
import { useSubscriptionsStore } from '@/stores/subscriptions';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';
import { loadStripe } from '@stripe/stripe-js';

const stripe = ref(null);
const elements = ref(null);
const cardElement = ref(null);
const cardComplete = ref(false);
const cardError = ref('');
const deleteCardError = ref('');

const paymentsStore = usePaymentsStore();
const { paymentMethods } = storeToRefs(paymentsStore);
const { transactions } = storeToRefs(paymentsStore);

const subscriptionsStore = useSubscriptionsStore();
const { allSubscriptions } = storeToRefs(subscriptionsStore);

const optionsStore = useOptionsStore();
const { provinceOptions } = storeToRefs(optionsStore);

const showAddCardForm = ref(false);

const billingAddress = ref({
    line1: '',
    line2: '',
    city: '',
    postalCode: '',
    country: ''
});

watch(showAddCardForm, async (visible) => {
    if (visible) {
        if (!stripe.value) {
            stripe.value = await loadStripe(import.meta.env.VITE_STRIPE_KEY);
        }
        await nextTick();
        if (stripe.value && !cardElement.value) {
            elements.value = stripe.value.elements();
            cardElement.value = elements.value.create('card');
            cardElement.value.mount('#card-element');
            cardElement.value.on('change', (event) => {
                cardComplete.value = event.complete;
                cardError.value = event.error ? event.error.message : '';
            });
        }
    } else {
        if (cardElement.value) {
            cardElement.value.unmount();
            cardElement.value = null;
            cardComplete.value = false;
            cardError.value = '';
        }
    }
});

const openAddCardForm = () => {
    cardError.value = '';
    showAddCardForm.value = true;
};

const closeAddCardForm = () => {
    cardError.value = '';
    showAddCardForm.value = false;
};

const save = async () => {
    if (!stripe.value || !elements.value) {
        return;
    }

    cardError.value = '';
    try {
        const { paymentMethod, error } = await stripe.value.createPaymentMethod({
            type: 'card',
            card: cardElement.value,
        });
        if (error) {
            cardError.value = error.message;
            return;
        }
        await paymentsStore.storePaymentMethod({
            payment_method_id: paymentMethod.id,
            billing_address_line_1: billingAddress.value.line1,
            billing_address_line_2: billingAddress.value.line2,
            billing_address_city: billingAddress.value.city,
            billing_address_postal_code: billingAddress.value.postalCode,
            billing_address_country: billingAddress.value.country,
            province_id: billingAddress.value.provinceId,
        });
        closeAddCardForm();
    } catch (err) {
        cardError.value = err.message || 'Error saving payment method';
    }
};

const defaultLoadingId = ref(null);
const removeLoadingId = ref(null);

const setDefault = async (method) => {
    if (!method || method.is_default) {
        return;
    }
    cardError.value = '';
    defaultLoadingId.value = method.id;
    try {
        await paymentsStore.makeDefaultPaymentMethod(method.id);
    } catch (err) {
        cardError.value = err?.response?.data?.message || err?.message || 'Error setting default payment method';
    } finally {
        defaultLoadingId.value = null;
    }
};

const showRemoveModal = ref(false);
const methodToRemove = ref(null);

const confirmRemovePaymentMethod = (method) => {
    methodToRemove.value = method;
    showRemoveModal.value = true;
};

const actuallyRemovePaymentMethod = async () => {
    if (!methodToRemove.value) {
        return;
    }
    await removePaymentMethod(methodToRemove.value);
    showRemoveModal.value = false;
    methodToRemove.value = null;
};

const removePaymentMethod = async (method) => {
    if (!method) {
        return;
    }
    deleteCardError.value = '';
    removeLoadingId.value = method.id;
    // If default, check for active subscriptions
    if (method.is_default) {
        const hasActive = allSubscriptions.value && allSubscriptions.value.some(sub => sub.is_current);
        if (hasActive) {
            deleteCardError.value = 'You must unsubscribe from all plans before deleting your default payment method.';
            removeLoadingId.value = null;
            return;
        }
    }
    try {
        await paymentsStore.removePaymentMethod(method.id);
    } catch (err) {
        deleteCardError.value = err?.response?.data?.message || err?.message || 'Error removing payment method';
    } finally {
        removeLoadingId.value = null;
    }
};

const formatExpiry = (month, year) => {
    if (!month || !year) {
        return '';
    }
    const m = String(month).padStart(2, '0');
    const y = String(year).slice(-2);
    return `${m}/${y}`;
};

const getDate = (row) => {
    return row.paid_at ? new Date(row.paid_at).toLocaleDateString() : new Date(row.created_at).toLocaleDateString();
};

const getAmount = (row) => {
    return row.amount + ' ' + (row.currency ? row.currency.toUpperCase() : '');
};

const getStatus = (row) => {
    return row.global_status?.name || row.global_status?.slug || row.global_status_id || 'Unknown';
};

const getTransactionId = (row) => row.invoice_number;

const downloadInvoicePdf = (transaction) => {
    if (transaction.invoice_pdf) {
        window.open(transaction.invoice_pdf, '_blank');
    } else {
        alert('PDF not available');
    }
};
</script>

<template>
<div>
    <h2 class="mb-3 text-2xl font-semibold">{{ $t('label.payments') }}</h2>
    <div v-if="deleteCardError" class="text-red-500 mb-2">{{ deleteCardError }}</div>
    <div v-if="paymentMethods && paymentMethods.length" class="mb-6">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div
                v-for="method in paymentMethods"
                :key="method.id"
                class="flex items-center justify-between rounded-lg border border-blue-100 bg-white p-4 shadow-md">
                <div class="flex items-center">
                    <div class="mr-3 flex h-10 w-10 items-center justify-center rounded-md bg-gray-100 text-sm font-semibold uppercase text-gray-700">
                        {{ method.card_type || method.provider || 'CARD' }}
                    </div>
                    <div>
                        <div class="text-base md:text-lg font-medium text-gray-900">
                            {{ $t('label.****') }} {{ method.card_number_last_4 }}
                        </div>
                        <div class="text-sm md:text-base text-gray-500">
                            {{ $t('label.expires') }}: {{ formatExpiry(method.expiry_month, method.expiry_year) }}
                        </div>
                    </div>
                </div>
                <div class="ml-3 flex flex-col gap-2 items-end">
                    <StyledButton
                        v-if="!method.is_default"
                        size="sm"
                        class="custom-tab-btn"
                        :disabled="defaultLoadingId === method.id"
                        @click="setDefault(method)">
                        {{ defaultLoadingId === method.id ? ($t('label.saving') || 'Saving...') : ($t('label.make_default') || 'Make default') }}
                    </StyledButton>
                    <span v-else class="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-sm md:text-base font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                        {{ $t('label.default') }}
                    </span>
                    <StyledButton
                        size="sm"
                        variant="danger"
                        class="custom-tab-btn-text"
                        :disabled="removeLoadingId === method.id"
                        @click="confirmRemovePaymentMethod(method)">
                        {{ removeLoadingId === method.id ? ($t('label.removing') || 'Removing...') : ($t('label.remove') || 'Remove') }}
                    </StyledButton>
                </div>
            </div>
        </div>
    </div>
    <div v-else-if="!showAddCardForm" class="mb-6">
        <p>{{ $t('label.no_payment_methods_yet') }}</p>
    </div>

    <div v-if="showAddCardForm" class="my-4">
        <form novalidate @submit.prevent="save">
            <div id="card-element" class="mb-8 p-4" />
            <div v-if="cardError" class="text-red-500 mb-2">{{ cardError }}</div>
            <!-- Billing Address Fields -->
            <StyledInput
                v-model="billingAddress.line1"
                :required="false"
                :label="$t('label.address_line_1')"
                :placeholder="$t('label.address_line_1')"
                name="billing_address_line_1"
                class="mb-2" />
            <StyledInput
                v-model="billingAddress.line2"
                :required="false"
                :label="$t('label.address_line_2')"
                :placeholder="$t('label.address_line_2')"
                name="billing_address_line_2"
                class="mb-2" />
            <StyledInput
                v-model="billingAddress.city"
                :required="false"
                :label="$t('label.city')"
                :placeholder="$t('label.city')"
                name="billing_address_city"
                class="mb-2" />
            <StyledInput
                v-model="billingAddress.postalCode"
                :required="false"
                :label="$t('label.postal_code')"
                :placeholder="$t('label.postal_code')"
                name="billing_address_postal_code"
                class="mb-2" />
            <StyledInput
                v-model="billingAddress.country"
                :required="false"
                :label="$t('label.country')"
                :placeholder="$t('label.country')"
                name="billing_address_country"
                class="mb-2" />
            <StyledDropdown
                v-model="billingAddress.provinceId"
                :required="false"
                :options="provinceOptions"
                option-label="name"
                option-value="id"
                :label="$t('label.province')"
                name="province_id"
                class="mb-2" />

            <StyledButton 
                type="submit" 
                class="mr-4 custom-tab-btn"
                :disabled="!cardComplete">{{ $t('label.save_card') }}</StyledButton>
            <StyledButton   
                type="button" 
                variant="outlined" 
                @click="closeAddCardForm">
                {{ $t('label.cancel') }}
            </StyledButton>
        </form>
    </div>
    <StyledButton v-if="!showAddCardForm" class="custom-tab-btn" @click="openAddCardForm">{{ $t('label.add_payment_card') }}</StyledButton>

    <h2 class="mb-4 mt-4 text-2xl font-semibold">{{ $t('label.payment_history') }}</h2>
    <DataTable
        :data="{ data: transactions }"
        :headers="[
            { title: $t('label.date'), key: 'date', value: getDate, sortable: true },
            { title: $t('label.amount'), key: 'amount', value: getAmount, sortable: true },
            { title: $t('label.status'), key: 'status', value: getStatus, sortable: true },
            { title: $t('label.transaction_id'), key: 'transaction_id', value: getTransactionId, sortable: true },
        ]" 
        :actions="[
            { title: 'Download PDF', icon: 'pdf', action: downloadInvoicePdf }
        ]" />

    <!-- Remove Payment Method Confirmation Modal -->
    <Transition name="modal">
        <div v-if="showRemoveModal" class="fixed inset-0 z-50 flex items-center justify-center bg-opacity-1">
            <div class="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
                <h3 class="mb-4 text-lg font-semibold text-gray-800">{{ $t('label.confirm_removal', { name: 'Payment Method' }) }}</h3>
                <p class="mb-6 text-gray-600">{{ $t('label.confirm_removal_message') }}</p>
                <div class="flex justify-end gap-2">
                    <StyledButton class="custom-tab-btn-text" variant="outlined" @click="showRemoveModal = false">
                        {{ $t('label.cancel') }}
                    </StyledButton>
                    <StyledButton class="custom-tab-btn-text" variant="danger" @click="actuallyRemovePaymentMethod">
                        {{ $t('label.remove') }}
                    </StyledButton>
                </div>
            </div>
        </div>
    </Transition>
</div>
</template>

<style scoped>
    .custom-tab-btn {
        background: #00594C !important;
    }

    .custom-tab-btn-text {
        color: #00594C !important;
        border-color: #00594C !important;
    }
</style>
